import {
  FC,
  useEffect,
  useRef,
  useState,
} from "react";

import { ArrowSmallDownIcon } from "@repo/edtech-assets";
import { useIsMobile } from "@repo/edtech-shared";
import { BackButton, Button } from "@repo/edtech-ui-kit";
import clsx from "clsx";

import { useHeightIndentationSync, usePlayersInteractions } from "../../../lib";
import {
  Course,
  LessonType,
  MOBILE_WIDTH,
  useAnalyticsContext,
  useIdContext,
  useMainContextActions,
  useMainContextValues,
} from "../../../model";
import { getConfig } from "../../../origins";
import { AcknowledgementBlock } from "../acknowledgement-block";
import { CourseContent } from "../course-content";
import { CourseProgress } from "../course-progress";
import { CourseSurvey } from "../course-survey";
import { Lesson } from "../lesson";
import { LessonDescription } from "../lesson-description";
import { LessonRating } from "../lesson-rating";

import st from "./CourseCompletion.module.scss";

type Props = {
  courseData: Course,
  backButtonPath?: string,
  hideRating: boolean,
  progressLessonsCount: number,
  progressCompletedLessonsCount: number,
};

export const CourseCompletion: FC<Props> = ({
  courseData,
  backButtonPath,
  hideRating,
  progressLessonsCount,
  progressCompletedLessonsCount,
}) => {
  const [ isCompleting, setIsCompleting ] = useState(false);
  const isMobile = useIsMobile(MOBILE_WIDTH);
  const { showLessonDescription } = getConfig();

  const { publicationId } = useIdContext();

  const {
    isNextLessonAvailable,
    library,
    isPrevLessonAvailable,
    currentLesson,
    isCoursePassed,
  } = useMainContextValues();

  const {
    nextLessonAction,
    prevLessonAction,
  } = useMainContextActions();

  const { showFinishLessonButton } = useMainContextValues();
  const { submitPendingFinish, isReadyToComplete } = useAnalyticsContext();

  const [ showSuccessBlock, setShowSuccessBlock ] = useState(false);
  const prevIsCoursePassedRef = useRef(false);

  useEffect(() => {
    if (isCoursePassed && !prevIsCoursePassedRef.current && courseData?.pubSurvey) {
      setShowSuccessBlock(true);
    }
    prevIsCoursePassedRef.current = isCoursePassed;
  }, [ courseData?.pubSurvey, isCoursePassed ]);

  usePlayersInteractions();

  const lessonData = currentLesson;
  const lessonTitle = lessonData?.title;

  const getPath = () => {
    let path = currentLesson?.title || "";
    const parentNodeIds = [ ...(currentLesson?.parentNodeIds ?? []) ].reverse();

    if (parentNodeIds.length > 0) {
      parentNodeIds.forEach((nodeId) => {
        path = (library?.[ nodeId ]?.title || "") + ` / ${path}`;
      });
    }

    return path;
  };

  const handleComplete = async () => {
    if (isCompleting || !isReadyToComplete) return;
    setIsCompleting(true);
    try {
      await submitPendingFinish();
    } finally {
      setIsCompleting(false);
    }
  };

  const lessonPath = getPath();
  const showCourseSurvey = !!courseData.pubSurvey &&
    (courseData.surveyShowAlways || isCoursePassed) &&
    !courseData.pubSurveyPassed;

  const [
    asideRef,
    mainRef,
    dynamicIndentation,
  ] = useHeightIndentationSync();

  const handleCloseSuccessBlock = () => {
    setShowSuccessBlock(false);
  };

  return (
    <div className={st.container}>
      {
        backButtonPath && (
          <BackButton
            pageName={lessonPath}
            path={backButtonPath}
          />
        )
      }
      <section className={st.lesson}>
        <div className={st.lesson_content}>
          <div className={st.header}>
            {
              !!lessonTitle && (
                <h1 className={st.header_title}>
                  {lessonData?.title}
                </h1>
              )
            }
            <div className={st.nav}>
              <div className={st.nav_buttons}>
                <button
                  aria-label="Предыдущий урок"
                  className={st.nav_button}
                  disabled={!isPrevLessonAvailable}
                  onClick={prevLessonAction}
                >
                  <ArrowSmallDownIcon className={st.nav_button_icon} />
                </button>
                <button
                  aria-label="Следующий урок"
                  className={clsx(st.nav_button, st.nav_button)}
                  disabled={!isNextLessonAvailable}
                  onClick={nextLessonAction}
                >
                  <ArrowSmallDownIcon
                    className={
                      clsx(st.nav_button_icon,
                        st.nav_button_icon_forward)
                    }
                  />
                </button>
              </div>
              {isMobile && <CourseContent courseData={courseData} />}
            </div>
          </div>
          {
            lessonData?.unitDescription && showLessonDescription && (
              <LessonDescription
                lesson={lessonData}
                unitDescription={lessonData?.unitDescription}
              />
            )
          }
          {
            lessonData && (
              <div
                key={lessonData?.nodeId}
                ref={mainRef}
                className={st.main}
              >
                <Lesson
                  lesson={lessonData}
                  publicationId={publicationId}
                  showSuccessBlock={showSuccessBlock}
                  onCloseSuccessBlock={handleCloseSuccessBlock}
                />
                {
                  showFinishLessonButton && (
                    <div className={st.complete_button_wrapper}>
                      <Button
                        aria-label="Завершить урок"
                        className={st.complete_button}
                        disabled={!isReadyToComplete || isCompleting}
                        onClick={handleComplete}
                      >
                        Завершить урок
                      </Button>
                    </div>
                  )
                }
                {
                  isMobile && (
                    <CourseProgress
                      completedLessons={progressCompletedLessonsCount}
                      lessonsCount={progressLessonsCount}
                    />
                  )
                }
                {
                  isMobile && showCourseSurvey && (
                    <CourseSurvey surveyId={courseData.pubSurvey} />
                  )
                }
                <AcknowledgementBlock
                  material={lessonData.uid}
                  publication={publicationId}
                  unit={lessonData.unitid}
                />
                {
                  !(
                    lessonData.materialType === LessonType.Test ||
                    !lessonData.description
                  ) &&
                  showLessonDescription && (
                    <LessonDescription lesson={lessonData} />
                  )
                }
                {
                  !hideRating && (
                    <LessonRating
                      key={lessonData?.nodeId}
                      lesson={lessonData}
                    />
                  )
                }
              </div>
            )
          }
        </div>
        {
          !isMobile && (
            <aside
              ref={asideRef}
              className={st.lesson_aside}
              style={
                {
                  gap: dynamicIndentation,
                }
              }
            >
              <CourseContent
                isAside
                courseData={courseData}
              />
              <CourseProgress
                completedLessons={progressCompletedLessonsCount}
                lessonsCount={progressLessonsCount}
              />
              {
                showCourseSurvey && (
                  <CourseSurvey surveyId={courseData.pubSurvey} />
                )
              }
            </aside>
          )
        }
      </section>
    </div>
  );
};
